import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx } from "vue"

const _hoisted_1 = /*#__PURE__*/_createElementVNode("span", { class: "plus-sign" }, null, -1)

import {ref, watch} from "vue";
import {useI18n} from "vue3-i18n";
import ActivityScheduleForm from "@/components/activities/ActivityScheduleForm.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormInput from "../forms/FormInput.vue";
import {ActivityOccurrence} from "@/types/entities/activity/activityOccurrence";
import {Status} from "@/validation";
import {notifyError, notifySuccess} from "@/notify";
import {Guid} from "@/types";
import {SucceededOrNotResponse} from "@/types/responses";
import {useActivityOccurrenceService} from "@/inversify.config";


export default /*#__PURE__*/_defineComponent({
  __name: 'ActivityOccurrenceForm',
  props: {
    activityId: {},
    modelValue: {}
  },
  emits: ["update:modelValue", "validated", "updatedActivityOccurrences"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const activityOccurrenceService = useActivityOccurrenceService();

// eslint-disable-next-line no-undef
const props = __props

// eslint-disable-next-line
const emit = __emit;

// eslint-disable-next-line
__expose({
  //to call validation in parent.
  validateForm
})

const {t} = useI18n();

const formInputs = ref<any[]>([])
const inputValidationStatuses: any = {}

let activityOccurrences = ref<ActivityOccurrence[]>(props.modelValue && props.modelValue.length > 0
    ? props.modelValue
    : [new ActivityOccurrence()])

watch(activityOccurrences, () => validateForm(), {deep: true})
watch(props, (newValue) => {
  activityOccurrences.value = newValue.modelValue;
}, {deep: true, immediate: true})

function addActivityOccurrenceRow() {
  activityOccurrences.value.push(new ActivityOccurrence())
}

async function removeActivityOccurrenceRow(index: number) {
  let activityOccurrence = {...activityOccurrences.value[index]};
  if (!activityOccurrence.id?.length) activityOccurrences.value.splice(index, 1)
  else if (await deleteActivityOccurrence(activityOccurrence.id)) {
    activityOccurrences.value.splice(index, 1)
  }
}

function addFormInputRef(ref: typeof FormInput) {
  if (!formInputs.value.includes(ref) && ref)
    formInputs.value.push(ref)
}

async function handleValidation(name: string, validationStatus: Status) {
  inputValidationStatuses[name] = validationStatus.valid
}

async function validateForm() {
  formInputs.value.forEach((x: typeof FormInput) => x.validateInput())

  if (Object.values(inputValidationStatuses).some(x => x === false)) {
    emit("validated", "", {valid: false});
    notifyError(t('activityOccurrences.form.mustChooseDate'))
    return
  }

  const hasDuplicates = validateDuplicateActivityScheduleDates();
  if (hasDuplicates) {
    emit("validated", "", {valid: false});
    notifyError(t('activityOccurrences.form.saveDuplicateError'))
    return
  }

  emit("update:modelValue", activityOccurrences.value);
  emit("validated", "activityOccurenceForm", {valid: true});
}

function validateDuplicateActivityScheduleDates() {
  activityOccurrences.value.forEach((activity: ActivityOccurrence) => {
    if (!activity.endDate) activity.endDate = undefined
  });

  return activityOccurrences.value.filter((activity: ActivityOccurrence, index: number) =>
      activityOccurrences.value.some((otherActivity: ActivityOccurrence, otherIndex: number) =>
          index !== otherIndex &&
          activity.startDate?.split("T")[0] === otherActivity.startDate?.split("T")[0] &&
          activity.endDate?.split("T")[0] === otherActivity.endDate?.split("T")[0]
      )
  ).length > 0;
}

async function deleteActivityOccurrence(id?: string): Promise<boolean> {
  if (!Guid.isValid(id ?? "") || id == null) return false;

  let confirmDelete = confirm(t("activityOccurrences.form.deleteConfirmation"));
  if (!confirmDelete)
    return false;

  let result: SucceededOrNotResponse = await activityOccurrenceService.deleteActivityOccurrence(id);
  if (!result?.succeeded) {
    notifyError(t('activityOccurrences.form.deleteError'))
    return false
  }
  notifySuccess(t('activityOccurrences.form.deleteSuccess'))
  emit("updatedActivityOccurrences", activityOccurrences.value);
  return true;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(Fieldset, {
    title: _unref(t)('activityOccurrences.occurrences')
  }, {
    default: _withCtx(() => [
      (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(activityOccurrences), (item, i) => {
        return (_openBlock(), _createBlock(ActivityScheduleForm, {
          key: i,
          "activity-id": item.activity.id,
          "onUpdate:activityId": ($event: any) => ((item.activity.id) = $event),
          "end-date": item.endDate,
          "onUpdate:endDate": ($event: any) => ((item.endDate) = $event),
          id: item.id,
          "onUpdate:id": ($event: any) => ((item.id) = $event),
          "max-participants": item.activity.maxParticipants,
          "onUpdate:maxParticipants": ($event: any) => ((item.activity.maxParticipants) = $event),
          "occupied-places": item.occupiedPlaces,
          "onUpdate:occupiedPlaces": ($event: any) => ((item.occupiedPlaces) = $event),
          "start-date": item.startDate,
          "onUpdate:startDate": ($event: any) => ((item.startDate) = $event),
          index: i,
          onHandleValidation: handleValidation,
          onRegisterFormInputRef: addFormInputRef,
          onRemoveActivityOccurrenceRow: ($event: any) => (removeActivityOccurrenceRow(i))
        }, null, 8, ["activity-id", "onUpdate:activityId", "end-date", "onUpdate:endDate", "id", "onUpdate:id", "max-participants", "onUpdate:maxParticipants", "occupied-places", "onUpdate:occupiedPlaces", "start-date", "onUpdate:startDate", "index", "onRemoveActivityOccurrenceRow"]))
      }), 128)),
      _createElementVNode("button", {
        class: "btn btn--border util--ml-auto",
        type: "button",
        onClick: addActivityOccurrenceRow
      }, [
        _createTextVNode(_toDisplayString(_unref(t)("activityOccurrences.add")) + " ", 1),
        _hoisted_1
      ])
    ]),
    _: 1
  }, 8, ["title"]))
}
}

})